import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, Download, Loader2, MapPin, ArrowRight, Truck, RotateCcw, X } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';

/**
 * Serviceability, as a MERCHANT sees it.
 *
 * Deliberately a separate, smaller screen from the staff `ServiceabilityPage` rather than that page
 * with the buttons hidden: the staff one is a 750-line editing tool (upload, bulk enable/disable,
 * row edit, delete, coverage stats) and every one of those is refused for a merchant by the API.
 * What a merchant actually needs is three questions answered — can you deliver to this pincode, can
 * you pick up from here and deliver there, and give me the list for my couriers — so that is the
 * whole page.
 *
 * The API narrows every row to the couriers enabled on the caller's own account
 * (`ServiceabilityController.restrictTo`), so nothing here can show another merchant's carrier mix.
 *
 * IMPORTANT and easy to get wrong: a pincode with NO row for a courier is treated as SERVICEABLE
 * everywhere else in the platform (the booking form only blocks on an explicit "not serviceable"
 * row), because the courier pincode lists are only partly loaded. This screen says exactly that
 * instead of showing a bare "no results", which a merchant would read as "you don't deliver there".
 */

interface MerchantCourier {
  courierId: number;
  isActive: boolean;
  courier: { id: number; name: string; code: string; orderType: string };
}

interface Row {
  id: number; courierId: number; pincode: string;
  city?: string | null; state?: string | null; zone?: string | null; mode?: string | null; tatDays?: number | null;
  isCodAllowed: boolean; isPrepaidAllowed: boolean; isPickupAllowed: boolean; isReverseAllowed: boolean;
  isOda: boolean; isServiceable?: boolean; isActive: boolean;
  courier?: { id: number; name: string; code: string };
}

interface LaneItem {
  courierId: number;
  courier: { id: number; name: string; code: string; brand?: string };
  zone?: string | null; tatDays?: number | null; mode?: string | null;
  capabilities: { cod: boolean; prepaid: boolean; reverse: boolean; oda: boolean };
}

function Yes({ on }: { on: boolean }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${on ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-50 text-slate-400 border border-slate-200'}`}>
      {on ? 'Yes' : 'No'}
    </span>
  );
}

function Badge({ txt, tone = 'ok' }: { txt: string; tone?: 'ok' | 'warn' }) {
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${tone === 'warn' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
      {txt}
    </span>
  );
}

const sixDigits = (v: string) => v.replace(/\D+/g, '').slice(0, 6);

export function MerchantServiceabilityPage() {
  // "Can you deliver here?" — one pincode, answered courier by courier.
  const [checkPin, setCheckPin] = useState('');
  const [checked, setChecked] = useState<{ pincode: string; rows: Row[] } | null>(null);

  // "Pickup here, deliver there" — the lane view the booking team asks for.
  const [source, setSource] = useState('');
  const [dest, setDest] = useState('');
  const [lane, setLane] = useState<{ source: string; destination: string; items: LaneItem[] } | null>(null);

  // The full list for this merchant's couriers.
  const [courierIds, setCourierIds] = useState<(string | number)[]>([]);
  const [pincode, setPincode] = useState('');
  const [codOnly, setCodOnly] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data: myCouriers } = useQuery({
    queryKey: ['merchant', 'couriers'],
    queryFn: async () => (await api.get<MerchantCourier[]>('/merchant/couriers')).data,
  });
  const courierOptions = useMemo(
    () => (myCouriers ?? []).filter((c) => c.isActive !== false).map((c) => ({ value: c.courierId, label: c.courier?.name ?? `#${c.courierId}` })),
    [myCouriers],
  );

  const listParams = useMemo(() => {
    const p: any = { page, pageSize };
    if (courierIds.length === 1) p.courierId = courierIds[0];
    else if (courierIds.length > 1) p.courierIds = courierIds.join(',');
    if (pincode) p.pincode = pincode;
    if (codOnly) p.cod = codOnly;
    return p;
  }, [courierIds, pincode, codOnly, page]);

  const { data: list, isLoading } = useQuery({
    queryKey: ['merchant-serviceability', listParams],
    queryFn: async () => (await api.get<{ total: number; items: Row[] }>('/serviceability', { params: listParams })).data,
  });

  const runCheck = useMutation({
    mutationFn: async () => {
      const pin = sixDigits(checkPin);
      if (pin.length !== 6) throw new Error('Enter a 6-digit pincode');
      const r = await api.get<{ items: Row[] }>('/serviceability', { params: { pincode: pin, pageSize: 200 } });
      return { pincode: pin, rows: r.data.items ?? [] };
    },
    onSuccess: (d) => setChecked(d),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? 'Could not check that pincode'),
  });

  const runLane = useMutation({
    mutationFn: async () => {
      const s = sixDigits(source); const d = sixDigits(dest);
      if (s.length !== 6 || d.length !== 6) throw new Error('Enter both pincodes in full (6 digits)');
      return (await api.get('/serviceability/check', { params: { source: s, destination: d } })).data;
    },
    onSuccess: (d: any) => setLane(d),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? 'Lookup failed'),
  });

  function resetList() { setCourierIds([]); setPincode(''); setCodOnly(''); setPage(1); }

  function download() {
    const p = new URLSearchParams();
    if (courierIds.length === 1) p.set('courierId', String(courierIds[0]));
    else if (courierIds.length > 1) p.set('courierIds', courierIds.join(','));
    if (pincode) p.set('pincode', pincode);
    if (codOnly) p.set('cod', codOnly);
    const qs = p.toString();
    downloadBlob('/serviceability/export' + (qs ? `?${qs}` : ''), `serviceability-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  const total = list?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const noCouriers = (myCouriers ?? []).length === 0;

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Serviceability</h1>
          <p className="text-sm text-slate-500">Check whether we deliver to a pincode, and what your couriers can do there.</p>
        </div>
        <button onClick={download} className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
          <Download size={14} /> Download list
        </button>
      </div>

      {noCouriers && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
          No courier is enabled on your account yet, so there is nothing to show here. Please contact Gaint Logistic to have one enabled.
        </div>
      )}

      {/* 1. Single pincode */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><MapPin size={15} className="text-emerald-600" /> Can we deliver to this pincode?</div>
        <div className="flex items-center gap-2">
          <input
            value={checkPin}
            onChange={(e) => setCheckPin(sixDigits(e.target.value))}
            onKeyDown={(e) => { if (e.key === 'Enter') runCheck.mutate(); }}
            placeholder="Delivery pincode, e.g. 110001"
            className="input w-56 font-mono"
          />
          <button onClick={() => runCheck.mutate()} disabled={runCheck.isPending}
            className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-60">
            {runCheck.isPending ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Check
          </button>
          {checked && (
            <button onClick={() => { setChecked(null); setCheckPin(''); }} className="px-2 py-2 text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
              <X size={12} /> Clear
            </button>
          )}
        </div>

        {checked && (
          <div className="mt-3">
            {checked.rows.length === 0 ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600">
                We have no pincode list loaded for your couriers at <span className="font-mono font-semibold">{checked.pincode}</span>.
                Orders to this pincode are still accepted — the courier confirms it at pickup.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                {checked.rows.map((r) => {
                  const ok = r.isActive !== false && r.isServiceable !== false;
                  return (
                    <div key={r.id} className={`border rounded-lg p-2.5 ${ok ? 'border-emerald-200 bg-emerald-50/40' : 'border-rose-200 bg-rose-50/40'}`}>
                      <div className="flex items-center gap-2">
                        <Truck size={14} className={ok ? 'text-emerald-600' : 'text-rose-500'} />
                        <div className="text-sm font-medium text-slate-800 truncate">{r.courier?.name ?? `#${r.courierId}`}</div>
                      </div>
                      <div className="text-xs text-slate-600 mt-1">
                        {ok ? 'Delivers here' : 'Does not deliver here'}
                        {r.tatDays != null && <> · {r.tatDays} day{r.tatDays === 1 ? '' : 's'}</>}
                        {r.zone && <> · Zone {r.zone}</>}
                      </div>
                      <div className="flex flex-wrap items-center gap-1 mt-1.5">
                        {r.isCodAllowed && <Badge txt="COD" />}
                        {r.isPrepaidAllowed && <Badge txt="Prepaid" />}
                        {r.isReverseAllowed && <Badge txt="Reverse" />}
                        {r.isOda && <Badge txt="ODA — extra charge" tone="warn" />}
                      </div>
                      {(r.city || r.state) && <div className="text-[11px] text-slate-400 mt-1">{[r.city, r.state].filter(Boolean).join(', ')}</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 2. Pickup → delivery */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <div className="text-sm font-semibold text-slate-800 mb-2 flex items-center gap-1.5"><ArrowRight size={15} className="text-emerald-600" /> Pickup to delivery</div>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={source} onChange={(e) => setSource(sixDigits(e.target.value))} placeholder="Pickup pincode" className="input w-44 font-mono" />
          <ArrowRight size={14} className="text-slate-400" />
          <input value={dest} onChange={(e) => setDest(sixDigits(e.target.value))}
            onKeyDown={(e) => { if (e.key === 'Enter') runLane.mutate(); }}
            placeholder="Delivery pincode" className="input w-44 font-mono" />
          <button onClick={() => runLane.mutate()} disabled={runLane.isPending}
            className="px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-60">
            {runLane.isPending ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Check
          </button>
          {lane && (
            <button onClick={() => setLane(null)} className="px-2 py-2 text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1"><X size={12} /> Clear</button>
          )}
        </div>

        {lane && (
          <div className="mt-3">
            <div className="text-sm text-slate-700 mb-2">
              {lane.items.length > 0
                ? <><strong>{lane.items.length}</strong> of your couriers can pick up at <span className="font-mono">{lane.source}</span> and deliver to <span className="font-mono">{lane.destination}</span>.</>
                : <>None of your couriers has both pickup at <span className="font-mono">{lane.source}</span> and delivery at <span className="font-mono">{lane.destination}</span> loaded. You can still book the order — the courier confirms at pickup.</>}
            </div>
            {lane.items.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                {lane.items.map((it) => (
                  <div key={it.courierId} className="border border-slate-200 rounded-lg p-2.5 flex items-center gap-2">
                    <Truck size={14} className="text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{it.courier?.name}</div>
                      <div className="text-xs text-slate-500 flex flex-wrap items-center gap-1.5">
                        <span>{it.tatDays != null ? `${it.tatDays} day${it.tatDays === 1 ? '' : 's'}` : 'Days not set'}</span>
                        {it.zone && <span>· Zone {it.zone}</span>}
                        {it.capabilities.cod && <Badge txt="COD" />}
                        {it.capabilities.reverse && <Badge txt="Reverse" />}
                        {it.capabilities.oda && <Badge txt="ODA" tone="warn" />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 3. The list */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Courier</label>
            <MultiSelectFilter
              label=""
              placeholder="All your couriers"
              values={courierIds}
              onChange={(vals) => { setCourierIds(vals); setPage(1); }}
              options={courierOptions}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Pincode</label>
            <input value={pincode} onChange={(e) => { setPincode(e.target.value.replace(/[^\d,\s]/g, '')); setPage(1); }}
              placeholder="110001 or 110001,400001" className="input w-full font-mono" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">COD</label>
            <select value={codOnly} onChange={(e) => { setCodOnly(e.target.value); setPage(1); }} className="input w-full">
              <option value="">Any</option>
              <option value="1">COD allowed</option>
              <option value="0">No COD</option>
            </select>
          </div>
          <div>
            <button onClick={resetList} className="px-3 py-2 border border-slate-200 rounded-lg text-xs flex items-center gap-1.5"><RotateCcw size={12} /> Reset</button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Courier</th>
                <th className="text-left px-3 py-2">Pincode</th>
                <th className="text-left px-3 py-2">City / State</th>
                <th className="text-left px-3 py-2">Zone</th>
                <th className="text-left px-3 py-2">Days</th>
                <th className="text-left px-3 py-2">COD</th>
                <th className="text-left px-3 py-2">Prepaid</th>
                <th className="text-left px-3 py-2">Pickup</th>
                <th className="text-left px-3 py-2">Reverse</th>
                <th className="text-left px-3 py-2">ODA</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400"><Loader2 size={16} className="animate-spin inline" /> Loading…</td></tr>
              )}
              {!isLoading && (list?.items ?? []).length === 0 && (
                <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-500">
                  {noCouriers ? 'No courier is enabled on your account yet.' : 'No pincodes found for this search.'}
                </td></tr>
              )}
              {(list?.items ?? []).map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-slate-800">{r.courier?.name ?? `#${r.courierId}`}</td>
                  <td className="px-3 py-2 font-mono">{r.pincode}</td>
                  <td className="px-3 py-2 text-slate-600">{[r.city, r.state].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-3 py-2">{r.zone || '—'}</td>
                  <td className="px-3 py-2">{r.tatDays ?? '—'}</td>
                  <td className="px-3 py-2"><Yes on={r.isCodAllowed} /></td>
                  <td className="px-3 py-2"><Yes on={r.isPrepaidAllowed} /></td>
                  <td className="px-3 py-2"><Yes on={r.isPickupAllowed} /></td>
                  <td className="px-3 py-2"><Yes on={r.isReverseAllowed} /></td>
                  <td className="px-3 py-2">{r.isOda ? <Badge txt="ODA" tone="warn" /> : <span className="text-slate-400 text-xs">No</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 text-xs text-slate-500">
          <div>{total.toLocaleString()} pincode row{total === 1 ? '' : 's'} for your couriers</div>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40">Previous</button>
            <span>Page {page} of {lastPage}</span>
            <button disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}
              className="px-2 py-1 border border-slate-200 rounded disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>

      <p className="text-[11px] text-slate-400 mt-3">
        A pincode that is not in the list is not a refusal — courier pincode lists are only partly loaded, so those orders are
        accepted and confirmed at pickup. ODA means an out-of-delivery-area location, which carries an extra charge.
      </p>
    </div>
  );
}
